import {
	STANDARD_FEE_TIERS,
	normalizeFeeTierFromFractionOrDisplay,
} from "@/lib/pools/format-fee-tier";

import { getTokenCategory, normalizePoolDisplayPair } from "./pair-order";
import type { PoolTokenCategory } from "./types";

export interface Pool {
	pool: string;
	/** Normalized display-first token symbol. */
	token1: string;
	/** Normalized display-second token symbol. */
	token2: string;
	token1Category: PoolTokenCategory;
	token2Category: PoolTokenCategory;
	uniswap: string;
	network: string;
	feeTier: string;
	tvl: number;
	/** Trailing 24h volume in USD when the data source provides it (e.g. subgraph `volumeUSD`). */
	volume1d?: number;
	/** Trailing 7d volume in USD from the workers catalog when exported. */
	volume7d?: number;
	/** Trailing 30d volume in USD from the workers catalog when exported. */
	volume30d?: number;
	apr1d: string;
	apr7d: string;
	apr30d: string;
	/** Token0 address (lower addr). Used for Uniswap create position link. */
	token0Address?: string;
	/** Token1 address (higher addr). Used for Uniswap create position link. */
	token1Address?: string;
	/** On-chain token0 symbol from subgraph (canonical order; survives display normalization). */
	onChainToken0Symbol?: string;
	/** On-chain token1 symbol from subgraph (canonical order; survives display normalization). */
	onChainToken1Symbol?: string;
	/** CoinGecko asset ID matched to canonical token0, when available. */
	coinGeckoToken0Id?: string;
	/** CoinGecko asset ID matched to canonical token1, when available. */
	coinGeckoToken1Id?: string;
	/** Global CoinGecko market-cap rank for canonical token0. */
	token0MarketCapRank?: number | null;
	/** Global CoinGecko market-cap rank for canonical token1. */
	token1MarketCapRank?: number | null;
	/** Global CoinGecko market cap in USD for canonical token0. */
	token0MarketCapUsd?: number | null;
	/** Global CoinGecko market cap in USD for canonical token1. */
	token1MarketCapUsd?: number | null;
	/** CoinGecko asset ID matched to display token1, when available. */
	coinGeckoDisplayToken1Id?: string;
	/** CoinGecko asset ID matched to display token2, when available. */
	coinGeckoDisplayToken2Id?: string;
	/** Global CoinGecko market-cap rank for display token1. */
	displayToken1MarketCapRank?: number | null;
	/** Global CoinGecko market-cap rank for display token2. */
	displayToken2MarketCapRank?: number | null;
	/** Global CoinGecko market cap in USD for display token1. */
	displayToken1MarketCapUsd?: number | null;
	/** Global CoinGecko market cap in USD for display token2. */
	displayToken2MarketCapUsd?: number | null;
}

/** Slim `/api/pools` list row — omits unused CoinGecko/canonical/USD fields. */
export type PoolsListItem = Pick<
	Pool,
	| "pool"
	| "token1"
	| "token2"
	| "token1Category"
	| "token2Category"
	| "uniswap"
	| "network"
	| "feeTier"
	| "tvl"
	| "volume1d"
	| "volume7d"
	| "volume30d"
	| "apr1d"
	| "apr7d"
	| "apr30d"
	| "token0Address"
	| "token1Address"
	| "onChainToken0Symbol"
	| "onChainToken1Symbol"
	| "coinGeckoDisplayToken1Id"
	| "coinGeckoDisplayToken2Id"
	| "displayToken1MarketCapRank"
	| "displayToken2MarketCapRank"
>;

export {
	getDefaultAnalyzeRangePct,
	getStablePrimarySelectedCoin,
	getTokenCategory,
} from "./pair-order";

export const FIXED_FEE_TIERS = STANDARD_FEE_TIERS;

import { SUPPORTED_POOL_NETWORKS } from "./supported-networks";

/** Networks supported in pool filters (independent of Redis catalog). */
const FILTER_NETWORKS = SUPPORTED_POOL_NETWORKS;

/** Protocol versions supported in pool filters. */
const FILTER_PROTOCOLS = ["V3", "V4"] as const;

function feeTierToPercent(feeTier: number | string): string {
	return normalizeFeeTierFromFractionOrDisplay(feeTier);
}

function ratioToApr(n: number): string {
	return (n * 100).toFixed(2) + "%";
}

const RAW_POOLS: Array<{
	pool: string;
	token_1: string;
	token_2: string;
	version: string;
	network: string;
	fee_tier: number | "Dynamic";
	count_of_trades: number;
	tvl_usd: number;
	fee_to_tvl_ratio_1day_1yr: number;
	fee_to_tvl_ratio_7days_1yr: number;
	fee_to_tvl_ratio_30days_1yr: number;
}> = [
	{
		pool: "0x13394005c1012e708fce1eb974f1130fdc73a5ce",
		token_1: "PYUSD",
		token_2: "USDC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 9.999999999999999e-5,
		count_of_trades: 4811,
		tvl_usd: 210797.706509,
		fee_to_tvl_ratio_1day_1yr: 0.40199430622514026,
		fee_to_tvl_ratio_7days_1yr: 0.27955435155465036,
		fee_to_tvl_ratio_30days_1yr: 0.13485556381101094,
	},
	{
		pool: "0x48da0965ab2d2cbf1c17c09cfb5cbe67ad5b1406",
		token_1: "DAI",
		token_2: "USDT",
		version: "v3",
		network: "Ethereum",
		fee_tier: 9.999999999999999e-5,
		count_of_trades: 14375,
		tvl_usd: 4289635.319202295,
		fee_to_tvl_ratio_1day_1yr: 0.3906893209621401,
		fee_to_tvl_ratio_7days_1yr: 0.28367162849739386,
		fee_to_tvl_ratio_30days_1yr: 0.12880372790971767,
	},
	{
		pool: "0x5777d92f208679db4b9778590fa3cab3ac9e2168",
		token_1: "DAI",
		token_2: "USDC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 9.999999999999999e-5,
		count_of_trades: 7270,
		tvl_usd: 1417526.6953632974,
		fee_to_tvl_ratio_1day_1yr: 0.31494946040908317,
		fee_to_tvl_ratio_7days_1yr: 0.20437854200151412,
		fee_to_tvl_ratio_30days_1yr: 0.15364786125979515,
	},
	{
		pool: "0x729565a1885d362be44f768dc9265711d149cd9155014be4f9ec36feeb25ef28",
		token_1: "USDC",
		token_2: "USDT",
		version: "v4",
		network: "Optimism",
		fee_tier: 2e-5,
		count_of_trades: 198506,
		tvl_usd: 124945.24610491894,
		fee_to_tvl_ratio_1day_1yr: 0.2857358667411483,
		fee_to_tvl_ratio_7days_1yr: 0.21084680777076065,
		fee_to_tvl_ratio_30days_1yr: 0.08146208718594584,
	},
	{
		pool: "0xd3f5c377a8506af29aeafbc90d1e5c6534f197b4",
		token_1: "PYUSD",
		token_2: "USDC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 3e-3,
		count_of_trades: 20,
		tvl_usd: 55054.075922,
		fee_to_tvl_ratio_1day_1yr: 0.24839620724372866,
		fee_to_tvl_ratio_7days_1yr: 0.07268002485769695,
		fee_to_tvl_ratio_30days_1yr: 0.01731362235977851,
	},
	{
		pool: "0x6c6bc977e13df9b0de53b251522280bb72383700",
		token_1: "DAI",
		token_2: "USDC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 5e-4,
		count_of_trades: 460,
		tvl_usd: 541621.8467534705,
		fee_to_tvl_ratio_1day_1yr: 0.24753676022835086,
		fee_to_tvl_ratio_7days_1yr: 0.13727857278415323,
		fee_to_tvl_ratio_30days_1yr: 0.0686400362672281,
	},
	{
		pool: "0xcecc13faad121d6e3ab33e137a746b512ab8ff1ace47da534aedb53e0b9fe9f8",
		token_1: "USDC",
		token_2: "USDS",
		version: "v4",
		network: "Ethereum",
		fee_tier: 9.999999999999999e-5,
		count_of_trades: 1580,
		tvl_usd: 95088.23701611062,
		fee_to_tvl_ratio_1day_1yr: 0.22576391877461424,
		fee_to_tvl_ratio_7days_1yr: 0.1722878419494727,
		fee_to_tvl_ratio_30days_1yr: 0.08077122441275308,
	},
	{
		pool: "0xbe3ad6a5669dc0b8b12febc03608860c31e2eef6",
		token_1: "USDC",
		token_2: "USD₮0",
		version: "v3",
		network: "Arbitrum",
		fee_tier: 9.999999999999999e-5,
		count_of_trades: 98214,
		tvl_usd: 3390773.146411,
		fee_to_tvl_ratio_1day_1yr: 0.17946195979399412,
		fee_to_tvl_ratio_7days_1yr: 0.11621700000051163,
		fee_to_tvl_ratio_30days_1yr: 0.1307705081969655,
	},
	{
		pool: "0x6f48eca74b38d2936b02ab603ff4e36a6c0e3a77",
		token_1: "DAI",
		token_2: "USDT",
		version: "v3",
		network: "Ethereum",
		fee_tier: 5e-4,
		count_of_trades: 492,
		tvl_usd: 167400.17195411,
		fee_to_tvl_ratio_1day_1yr: 0.16561912046474217,
		fee_to_tvl_ratio_7days_1yr: 0.0954225159990372,
		fee_to_tvl_ratio_30days_1yr: 0.06945441597447014,
	},
	{
		pool: "0x09d677e7efcc950e323ee70b5993f80281603f6c8a10851aa3a18b65819ec4ed",
		token_1: "USDC",
		token_2: "USDS",
		version: "v4",
		network: "Ethereum",
		fee_tier: 7.5e-5,
		count_of_trades: 1186,
		tvl_usd: 14667.990517215374,
		fee_to_tvl_ratio_1day_1yr: 0.14778172876654638,
		fee_to_tvl_ratio_7days_1yr: 0.10203034616537461,
		fee_to_tvl_ratio_30days_1yr: 0.043976365643076096,
	},
	// BTC/Stablecoin pairs
	{
		pool: "0x7067a500ad68cf5129b957fa62c97042cddccaa15d16cb91e3720d97971109bb",
		token_1: "USDC",
		token_2: "cbBTC",
		version: "v4",
		network: "Ethereum",
		fee_tier: 3e-3,
		count_of_trades: 1699,
		tvl_usd: 21950.3240562674,
		fee_to_tvl_ratio_1day_1yr: 492.3933796590077,
		fee_to_tvl_ratio_7days_1yr: 84.0237512904474,
		fee_to_tvl_ratio_30days_1yr: 19.659647600019618,
	},
	{
		pool: "0x108a840f633c00927f0b773c8851cc1835ae7631833e88d7a3c38fd761ef2f7a",
		token_1: "USDC",
		token_2: "WBTC",
		version: "v4",
		network: "Arbitrum",
		fee_tier: 1e-2,
		count_of_trades: 1872,
		tvl_usd: 38213.04920376881,
		fee_to_tvl_ratio_1day_1yr: 13.231168350504369,
		fee_to_tvl_ratio_7days_1yr: 3.051458509717813,
		fee_to_tvl_ratio_30days_1yr: 0.910478156096323,
	},
	{
		pool: "0xec558e484cc9f2210714e345298fdc53b253c27d",
		token_1: "USDC",
		token_2: "cbBTC",
		version: "v3",
		network: "Base",
		fee_tier: 3e-3,
		count_of_trades: 31457,
		tvl_usd: 960714.5284223984,
		fee_to_tvl_ratio_1day_1yr: 7.205167741278416,
		fee_to_tvl_ratio_7days_1yr: 2.8183501781003297,
		fee_to_tvl_ratio_30days_1yr: 0.9351557460562396,
	},
	{
		pool: "0x4a24fcf3c1cbebd80d09537c7aa15adf2d228aa1b9ba1b0e5fb82e66dc96c0d2",
		token_1: "USDT",
		token_2: "WBTC",
		version: "v4",
		network: "Ethereum",
		fee_tier: 5e-4,
		count_of_trades: 38589,
		tvl_usd: 290804.6857508497,
		fee_to_tvl_ratio_1day_1yr: 7.084148545805525,
		fee_to_tvl_ratio_7days_1yr: 4.348626575286495,
		fee_to_tvl_ratio_30days_1yr: 2.3864250767061175,
	},
	{
		pool: "0xadab76dd2dca7ae080a796f0ce86170e482afb4a",
		token_1: "USDC",
		token_2: "WBTC",
		version: "v3",
		network: "Optimism",
		fee_tier: 3e-3,
		count_of_trades: 22481,
		tvl_usd: 371370.8624977264,
		fee_to_tvl_ratio_1day_1yr: 5.232872562737553,
		fee_to_tvl_ratio_7days_1yr: 1.4487268281110035,
		fee_to_tvl_ratio_30days_1yr: 0.5722500028845259,
	},
	{
		pool: "0x6985cb98ce393fce8d6272127f39013f61e36166",
		token_1: "USDC",
		token_2: "WBTC",
		version: "v3",
		network: "Arbitrum",
		fee_tier: 3e-3,
		count_of_trades: 13125,
		tvl_usd: 155607.2839356144,
		fee_to_tvl_ratio_1day_1yr: 4.159937977262759,
		fee_to_tvl_ratio_7days_1yr: 2.237563261791979,
		fee_to_tvl_ratio_30days_1yr: 0.8103485346805356,
	},
	{
		pool: "0xfbb6eed8e7aa03b138556eedaf5d271a5e1e43ef",
		token_1: "USDC",
		token_2: "cbBTC",
		version: "v3",
		network: "Base",
		fee_tier: 5e-4,
		count_of_trades: 237169,
		tvl_usd: 4267796.776428845,
		fee_to_tvl_ratio_1day_1yr: 2.1827105602630055,
		fee_to_tvl_ratio_7days_1yr: 1.1065588158366508,
		fee_to_tvl_ratio_30days_1yr: 0.6156021171172632,
	},
	{
		pool: "0x0e4831319a50228b9e450861297ab92dee15b44f",
		token_1: "USDC",
		token_2: "WBTC",
		version: "v3",
		network: "Arbitrum",
		fee_tier: 5e-4,
		count_of_trades: 330961,
		tvl_usd: 5512847.951978716,
		fee_to_tvl_ratio_1day_1yr: 1.8380551191168675,
		fee_to_tvl_ratio_7days_1yr: 1.2675088242201777,
		fee_to_tvl_ratio_30days_1yr: 0.6122906242925105,
	},
	{
		pool: "0xdfb2536ba09a004b32db0a1a15f73676b5e356d831c4ea1e843cd9433b080ab6",
		token_1: "USDC",
		token_2: "cbBTC",
		version: "v4",
		network: "Base",
		fee_tier: 3e-3,
		count_of_trades: 25832,
		tvl_usd: 27456.5669626069,
		fee_to_tvl_ratio_1day_1yr: 1.7269966757827564,
		fee_to_tvl_ratio_7days_1yr: 1.1121697683153577,
		fee_to_tvl_ratio_30days_1yr: 0.5730680398983032,
	},
	{
		pool: "0x56534741cd8b152df6d48adf7ac51f75169a83b2",
		token_1: "USDT",
		token_2: "WBTC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 5e-4,
		count_of_trades: 71670,
		tvl_usd: 19136315.03618306,
		fee_to_tvl_ratio_1day_1yr: 1.6693303891557048,
		fee_to_tvl_ratio_7days_1yr: 0.8839558321451088,
		fee_to_tvl_ratio_30days_1yr: 0.3878025370492206,
	},
	// ETH/Stablecoin pairs
	{
		pool: "0x6c561b446416e1a00e8e93e221854d6ea4171372",
		token_1: "USDC",
		token_2: "WETH",
		version: "v3",
		network: "Base",
		fee_tier: 3e-3,
		count_of_trades: 0,
		tvl_usd: 51965406.77513015,
		fee_to_tvl_ratio_1day_1yr: 1.3144013649187927,
		fee_to_tvl_ratio_7days_1yr: 2.535992751209939,
		fee_to_tvl_ratio_30days_1yr: 1.11441818195716,
	},
	{
		pool: "0xd934712639fede326a3ff8d2a9c2f73749bac510c23fb33443cea7f3c9aca6f3",
		token_1: "USDC",
		token_2: "ETH",
		version: "v4",
		network: "Ethereum",
		fee_tier: 1e-2,
		count_of_trades: 0,
		tvl_usd: 253702.98659190975,
		fee_to_tvl_ratio_1day_1yr: 1.2062743213093001,
		fee_to_tvl_ratio_7days_1yr: 1.5972129518034275,
		fee_to_tvl_ratio_30days_1yr: 0.8748584866558944,
	},
	{
		pool: "0x42fc852a750ba93d5bf772ecdc857e87a86403a9",
		token_1: "USDC",
		token_2: "WETH",
		version: "v3",
		network: "Arbitrum",
		fee_tier: 1e-2,
		count_of_trades: 0,
		tvl_usd: 187418.5466226252,
		fee_to_tvl_ratio_1day_1yr: 0.8785057801908311,
		fee_to_tvl_ratio_7days_1yr: 1.4140514647317974,
		fee_to_tvl_ratio_30days_1yr: 0.4961934921352372,
	},
	{
		pool: "0xe070797535b13431808f8fc81fdbe7b41362960ed0b55bc2b6117c49c51b7eb9",
		token_1: "USDC",
		token_2: "ETH",
		version: "v4",
		network: "Base",
		fee_tier: 3e-3,
		count_of_trades: 0,
		tvl_usd: 1011275.3646648922,
		fee_to_tvl_ratio_1day_1yr: 0.765664815857915,
		fee_to_tvl_ratio_7days_1yr: 0.5756108955550419,
		fee_to_tvl_ratio_30days_1yr: 0.1864386938868461,
	},
	{
		pool: "0x88e6a0c2ddd26feeb64f039a2c41296fcb3f5640",
		token_1: "USDC",
		token_2: "WETH",
		version: "v3",
		network: "Ethereum",
		fee_tier: 5e-4,
		count_of_trades: 0,
		tvl_usd: 72352709.21727544,
		fee_to_tvl_ratio_1day_1yr: 0.7234786248821803,
		fee_to_tvl_ratio_7days_1yr: 1.2288094190180325,
		fee_to_tvl_ratio_30days_1yr: 0.39269917416464806,
	},
	{
		pool: "0xc1738d90e2e26c35784a0d3e3d8a9f795074bca4",
		token_1: "USDC",
		token_2: "WETH",
		version: "v3",
		network: "Optimism",
		fee_tier: 3e-3,
		count_of_trades: 0,
		tvl_usd: 1296441.60004391,
		fee_to_tvl_ratio_1day_1yr: 0.7165507772629591,
		fee_to_tvl_ratio_7days_1yr: 1.9917491339392401,
		fee_to_tvl_ratio_30days_1yr: 0.9910135278024893,
	},
	{
		pool: "0x03df2300e83353309c1069ae3ea89c31b361a009f9c36a1de5c8f0afcc45bde8",
		token_1: "USDC",
		token_2: "ETH",
		version: "v4",
		network: "Arbitrum",
		fee_tier: 3e-3,
		count_of_trades: 0,
		tvl_usd: 281575.9423551199,
		fee_to_tvl_ratio_1day_1yr: 0.6514256484484174,
		fee_to_tvl_ratio_7days_1yr: 0.30108961341196605,
		fee_to_tvl_ratio_30days_1yr: 0.47744730556557347,
	},
	{
		pool: "0x4e68ccd3e89f51c3074ca5072bbac773960dfa36",
		token_1: "USDT",
		token_2: "WETH",
		version: "v3",
		network: "Ethereum",
		fee_tier: 3e-3,
		count_of_trades: 0,
		tvl_usd: 62662933.56811493,
		fee_to_tvl_ratio_1day_1yr: 0.609096246729531,
		fee_to_tvl_ratio_7days_1yr: 1.3424315630350478,
		fee_to_tvl_ratio_30days_1yr: 0.9283390573296171,
	},
	{
		pool: "0x641c00a822e8b671738d32a431a4fb6074e5c79d",
		token_1: "USD₮0",
		token_2: "WETH",
		version: "v3",
		network: "Arbitrum",
		fee_tier: 5e-4,
		count_of_trades: 0,
		tvl_usd: 14816648.151060801,
		fee_to_tvl_ratio_1day_1yr: 0.49862526357999176,
		fee_to_tvl_ratio_7days_1yr: 1.0357966134256935,
		fee_to_tvl_ratio_30days_1yr: 0.8412137655713132,
	},
	{
		pool: "0x8927058918e3cff6f55efe45a58db1be1f069e49",
		token_1: "USDC",
		token_2: "WETH",
		version: "v3",
		network: "Unichain",
		fee_tier: 3e-3,
		count_of_trades: 0,
		tvl_usd: 173417.29538892323,
		fee_to_tvl_ratio_1day_1yr: 0.47374038816493974,
		fee_to_tvl_ratio_7days_1yr: 1.2809464978654295,
		fee_to_tvl_ratio_30days_1yr: 0.634767604598043,
	},
	{
		pool: "0xe6195a1f1c8f5d0bcf0a880db26738a1df4f6863017700a8f6377a72d45366f2",
		token_1: "ETH",
		token_2: "cbBTC",
		version: "v4",
		network: "Base",
		fee_tier: 0.003,
		count_of_trades: 10682,
		tvl_usd: 144044.0261633249,
		fee_to_tvl_ratio_1day_1yr: 1.8082550540757718,
		fee_to_tvl_ratio_7days_1yr: 1.5235341162162825,
		fee_to_tvl_ratio_30days_1yr: 1.1758352562180496,
	},
	{
		pool: "0x5876423ef0d34b53e8ca5d6972ff9a9d72261eacf645a2fad664450f3eebc003",
		token_1: "ETH",
		token_2: "WBTC",
		version: "v4",
		network: "Optimism",
		fee_tier: 0.003,
		count_of_trades: 9349,
		tvl_usd: 91428.24813042619,
		fee_to_tvl_ratio_1day_1yr: 1.3468472985544424,
		fee_to_tvl_ratio_7days_1yr: 0.6931232806592801,
		fee_to_tvl_ratio_30days_1yr: 0.5480859263997329,
	},
	{
		pool: "0x6ab3bba2f41e7eaa262fa5a1a9b3932fa161526f",
		token_1: "WETH",
		token_2: "WBTC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 0.01,
		count_of_trades: 528,
		tvl_usd: 56129.17054889917,
		fee_to_tvl_ratio_1day_1yr: 0.39382525192335666,
		fee_to_tvl_ratio_7days_1yr: 0.3215234970836543,
		fee_to_tvl_ratio_30days_1yr: 0.35449366075532074,
	},
	{
		pool: "0x37ffd11972128fd624337ebceb167c8c0a5115ff",
		token_1: "WETH",
		token_2: "WBTC",
		version: "v3",
		network: "Optimism",
		fee_tier: 0.01,
		count_of_trades: 2013,
		tvl_usd: 141473.3945211959,
		fee_to_tvl_ratio_1day_1yr: 0.3331615143258118,
		fee_to_tvl_ratio_7days_1yr: 0.13463858673680884,
		fee_to_tvl_ratio_30days_1yr: 0.14044969802375276,
	},
	{
		pool: "0x8c7080564b5a792a33ef2fd473fba6364d5495e5",
		token_1: "WETH",
		token_2: "cbBTC",
		version: "v3",
		network: "Base",
		fee_tier: 0.003,
		count_of_trades: 26182,
		tvl_usd: 6485865.765111474,
		fee_to_tvl_ratio_1day_1yr: 0.32586950681772053,
		fee_to_tvl_ratio_7days_1yr: 0.4124095370152968,
		fee_to_tvl_ratio_30days_1yr: 0.48992294761232935,
	},
	{
		pool: "0x599326d9cd5e1ea0893305c87d5ec3199e7f05e94d72159f35dea381479a615e",
		token_1: "ETH",
		token_2: "WBTC",
		version: "v4",
		network: "Ethereum",
		fee_tier: 0.0005,
		count_of_trades: 39602,
		tvl_usd: 1440266.8982799335,
		fee_to_tvl_ratio_1day_1yr: 0.2987140289681445,
		fee_to_tvl_ratio_7days_1yr: 0.24540972443018236,
		fee_to_tvl_ratio_30days_1yr: 0.27045330969326464,
	},
	{
		pool: "0x2f5e87c9312fa29aed5c179e456625d79015299c",
		token_1: "WETH",
		token_2: "WBTC",
		version: "v3",
		network: "Arbitrum",
		fee_tier: 0.0005,
		count_of_trades: 425939,
		tvl_usd: 58361980.66448124,
		fee_to_tvl_ratio_1day_1yr: 0.2663057629786596,
		fee_to_tvl_ratio_7days_1yr: 0.28113644513743086,
		fee_to_tvl_ratio_30days_1yr: 0.29261722662556433,
	},
	{
		pool: "0x7aea2e8a3843516afa07293a10ac8e49906dabd1",
		token_1: "WETH",
		token_2: "cbBTC",
		version: "v3",
		network: "Base",
		fee_tier: 0.0005,
		count_of_trades: 159831,
		tvl_usd: 3564140.264859466,
		fee_to_tvl_ratio_1day_1yr: 0.2456746856490754,
		fee_to_tvl_ratio_7days_1yr: 0.22633091708744052,
		fee_to_tvl_ratio_30days_1yr: 0.2096950429973635,
	},
	{
		pool: "0x4585fe77225b41b697c938b018e2ac67ac5a20c0",
		token_1: "WETH",
		token_2: "WBTC",
		version: "v3",
		network: "Ethereum",
		fee_tier: 0.0005,
		count_of_trades: 82462,
		tvl_usd: 23415334.340448294,
		fee_to_tvl_ratio_1day_1yr: 0.2390996741132785,
		fee_to_tvl_ratio_7days_1yr: 0.17018129040000166,
		fee_to_tvl_ratio_30days_1yr: 0.17220903333208246,
	},
	{
		pool: "0x94d2848a5e65d397105a37758079b0a399e8d9a2cd332d5ab889660cc295df58",
		token_1: "ETH",
		token_2: "WBTC",
		version: "v4",
		network: "Arbitrum",
		fee_tier: 0.003,
		count_of_trades: 2389,
		tvl_usd: 47595.32573791705,
		fee_to_tvl_ratio_1day_1yr: 0.19934787953781713,
		fee_to_tvl_ratio_7days_1yr: 0.16474195189187146,
		fee_to_tvl_ratio_30days_1yr: 0.16736519732702917,
	},
];

/** Legacy seed list for scripts only — runtime catalog is Redis `pools:all`. */
export const POOLS_DATA: Pool[] = RAW_POOLS.map((r) =>
	normalizePoolDisplayPair({
		pool: r.pool,
		token1: r.token_1,
		token2: r.token_2,
		token1Category: getTokenCategory(r.token_1),
		token2Category: getTokenCategory(r.token_2),
		uniswap: r.version.toUpperCase(),
		network: r.network,
		feeTier: feeTierToPercent(r.fee_tier),
		tvl: r.tvl_usd,
		apr1d: ratioToApr(r.fee_to_tvl_ratio_1day_1yr),
		apr7d: ratioToApr(r.fee_to_tvl_ratio_7days_1yr),
		apr30d: ratioToApr(r.fee_to_tvl_ratio_30days_1yr),
	}),
);

export const getUniqueNetworks = (): string[] => [...FILTER_NETWORKS];

export const getUniqueProtocols = (): string[] => [...FILTER_PROTOCOLS];

export const getUniqueFeeTiers = (): string[] => {
	return [...FIXED_FEE_TIERS];
};
